import { useState, useEffect } from "react";
import { Icons } from "../../components/common/Icons";
import { SM, WD, daysIn, firstDay, fmtKey, fmtDisp } from "../../utils/helpers";
import api from "../../services/api";
import { useInternData } from "../../context/InternContext";
import { InternModal, StatusBadge } from "./InternWidgets";
import T from '../../assets/styles/theme';import "../../assets/styles/Intern/AttendanceWidget.css";
const internMO = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const internWD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function AttendanceWidget() {
  const { internData, attendance, dailyAttendance, monthlyAttStats, leaveSummary, initLeaveReqs, initPermReqs, refreshInternData } = useInternData();

  const [tab, setTab] = useState("summary");
  const [leaveReqs, setLeaveReqs] = useState(initLeaveReqs);
  const [permReqs, setPermReqs] = useState(initPermReqs);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [showPermForm, setShowPermForm] = useState(false);
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLeaveType, setFilterLeaveType] = useState("all");
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [sandwichWarn, setSandwichWarn] = useState("");
  const [sandwichConfirm, setSandwichConfirm] = useState(false);
  const [pendingLeaveData, setPendingLeaveData] = useState(null);
  const [leaveFromVal, setLeaveFromVal] = useState("");
  const [leaveToVal, setLeaveToVal]   = useState("");
  const [curMonth, setCurMonth] = useState(new Date().getMonth());
  const [monthStats, setMonthStats] = useState(null);
  const [dailyData, setDailyData] = useState({});
  const yr = new Date().getFullYear();

  useEffect(() => {if (initLeaveReqs?.length || initLeaveReqs?.length === 0) setLeaveReqs(initLeaveReqs);}, [initLeaveReqs]);
  useEffect(() => {if (initPermReqs?.length || initPermReqs?.length === 0) setPermReqs(initPermReqs);}, [initPermReqs]);

  useEffect(() => {
    api.getLeaveTypes().then((data) => {
      if (Array.isArray(data) && data.length) setLeaveTypes(data.filter((t) => t.is_active !== false && t.is_active !== 0));
    }).catch(() => {});
  }, []);

  const monthForFilter = filterMonth === 'all' ? curMonth + 1 : parseInt(filterMonth, 10);
  useEffect(() => {
    api.getMonthStats(monthForFilter, yr).then((data) => setMonthStats(data)).catch(() => {});
    api.getDailyAttendance(monthForFilter, yr).then((data) => {
      const map = {};
      (data || []).forEach((r) => {
        const d = r.date ? r.date.substring(0, 10) : '';
        if (d) map[d] = r.status;
      });
      setDailyData(map);
    }).catch(() => {});
  }, [monthForFilter, yr]);

  const checkSandwich = (from, to) => {
    if (!from || !to) { setSandwichWarn(""); return; }
    api.sandwichCheck(from, to)
      .then(data => setSandwichWarn(data?.isSandwich ? (data.reason || "This is a sandwich leave — will be counted as LWP (Leave Without Pay).") : ""))
      .catch(() => setSandwichWarn(""));
  };

  const submitLeaveAPI = async ({ selectedType, fromDate, toDate, reason }) => {
    try {
      await api.applyLeave({ leaveType: selectedType, fromDate, toDate, reason });
      await refreshInternData();
    } catch (err) { console.warn(err.message); }
    setShowLeaveForm(false);
    setSandwichWarn(""); setLeaveFromVal(""); setLeaveToVal("");
    setSandwichConfirm(false); setPendingLeaveData(null);
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const selectedType = fd.get("type") || "";
    const fromDate = fd.get("from") || "";
    const toDate = fd.get("to") || "";
    const reason = fd.get("reason") || "";
    if (sandwichWarn) {
      setPendingLeaveData({ selectedType, fromDate, toDate, reason });
      setSandwichConfirm(true);
      return;
    }
    await submitLeaveAPI({ selectedType, fromDate, toDate, reason });
  };
  const handlePermSubmit = async (e) => {
    e.preventDefault();const fd = new FormData(e.target);
    try {
      await api.applyPermission({ date: fd.get("date"), fromTime: fd.get("from"), toTime: fd.get("to"), reason: fd.get("reason") });
      await refreshInternData();
    } catch (err) {console.warn(err.message);}
    setShowPermForm(false);
  };

  const yearStr = String(yr);
  let filteredLeave = filterMonth === "all" ? leaveReqs : leaveReqs.filter((r) => (r.from || r.from_date || '').startsWith(`${yearStr}-${filterMonth}`));
  if (filterStatus !== "all") filteredLeave = filteredLeave.filter((r) => r.status === filterStatus);
  if (filterLeaveType !== "all") filteredLeave = filteredLeave.filter((r) => r.type === filterLeaveType);
  let filteredPerm = filterMonth === "all" ? permReqs : permReqs.filter((r) => (r.date || '').startsWith(`${yearStr}-${filterMonth}`));
  if (filterStatus !== "all") filteredPerm = filteredPerm.filter((r) => r.status === filterStatus);

  const getStats = () => {
    if (filterMonth === "all") return attendance;
    const ms = monthStats || monthlyAttStats?.[filterMonth];
    if (!ms) return { totalDays: 0, present: 0, leaves: 0, holidays: 0, wfh: 0, working: 0 };
    return { totalDays: ms.working || ms.totalDays || 0, present: ms.present || 0, leaves: ms.leave || ms.leaves || 0, holidays: ms.holiday || ms.holidays || 0, wfh: ms.wfh || 0, working: ms.working || 0 };
  };
  const stats = getStats();
  const totalDaysForRate = stats.totalDays || stats.working || 1;
  const attRate = totalDaysForRate > 0 ? Math.round((stats.present || 0) / totalDaysForRate * 100) : 0;
  const hasFilter = filterMonth !== "all" || filterStatus !== "all" || filterLeaveType !== "all";
  const clearAll = () => {setFilterMonth("all");setFilterStatus("all");setFilterLeaveType("all");};

  return (
    <div className="card animate-in delay-4">
      <div className="card-header"><div><div className="card-title">Attendance</div><div className="card-subtitle">Summary, leaves & permissions</div></div>
        <div className="attendancewidget-inline-1">
          <button className="btn-outline" onClick={() => setShowLeaveForm(true)}><Icons.Plus /> Leave</button>
          <button className="btn-outline" onClick={() => setShowPermForm(true)}><Icons.Plus /> Permission</button>
        </div>
      </div>
      <div className="card-body">
        <div className="tabs-row">
          {["summary", "calendar", "leave", "permission"].map((t) => <button key={t} className={`tab-btn ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{t === "summary" ? "Summary" : t === "calendar" ? "Calendar" : t === "leave" ? "Leave Requests" : "Permissions"}</button>)}
        </div>

        {/* ===== FILTER BAR ===== */}
        <div className="filter-bar">
          <span className="filter-icon"><Icons.Filter /> Filters</span>
          <select className="filter-select" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
            <option value="all">All Months</option>
            {SM.map((m, i) => <option key={i} value={String(i + 1).padStart(2, '0')}>{m} {yr}</option>)}
          </select>
          {(tab === "leave" || tab === "permission") && <>
            <div className="filter-divider" />
            {["all", "approved", "pending", "rejected"].map((s) =>
            <div key={s} className={`filter-pill ${filterStatus === s ? "on" : ""}`} onClick={() => setFilterStatus(filterStatus === s ? "all" : s)}>
                {s !== "all" && <span className="fp-dot attendancewidget-dyn-1" style={{ "--attendancewidget-1-background": s === "approved" ? "#2563eb" : s === "pending" ? "#d97706" : "#ef4444" }} />}
                {s === "all" ? "All Status" : s.charAt(0).toUpperCase() + s.slice(1)}
              </div>
            )}
          </>}
          {tab === "leave" && <>
            <div className="filter-divider" />
            {["all", "Casual Leave", "Sick Leave", "Personal Leave", "Comp Off"].map((t) =>
            <div key={t} className={`filter-pill ${filterLeaveType === t ? "on" : ""}`} onClick={() => setFilterLeaveType(filterLeaveType === t ? "all" : t)}>
                {t !== "all" && <span className="fp-dot attendancewidget-dyn-2" style={{ "--attendancewidget-2-background": leaveSummary.find((l) => l.type === t)?.color || "#94a3b8" }} />}
                {t === "all" ? "All Types" : t.replace(" Leave", "")}
              </div>
            )}
          </>}
          {hasFilter && <span className="filter-clear-btn" onClick={clearAll}>✕ Clear all</span>}
        </div>

        {tab === "summary" && <>
          <div className="att-stats">
            <div className="att-stat-card"><div className="att-stat-value primary">{stats.totalDays || stats.working}</div><div className="att-stat-label">Total Working Days</div></div>
            <div className="att-stat-card"><div className="att-stat-value success">{stats.present}</div><div className="att-stat-label">Days Present</div></div>
            <div className="att-stat-card"><div className="att-stat-value warning">{stats.leaves}</div><div className="att-stat-label">Leaves Taken</div></div>
            <div className="att-stat-card"><div className="att-stat-value info">{stats.holidays}</div><div className="att-stat-label">Holidays</div></div>
          </div>
          {filterMonth !== "all" && stats.wfh !== undefined &&
          <div className="attendancewidget-inline-2">
              <div className="att-stat-card"><div className="att-stat-value attendancewidget-dyn-3" style={{ "--attendancewidget-3-color": T.info }}>{stats.wfh}</div><div className="att-stat-label">WFH Days</div></div>
              <div className="att-stat-card"><div className="att-stat-value attendancewidget-dyn-4" style={{ "--attendancewidget-4-color": T.purple }}>{stats.permission}</div><div className="att-stat-label">Permissions</div></div>
            </div>
          }
          <div className="attendancewidget-inline-3">
            <div className="attendancewidget-inline-4"><span className="attendancewidget-inline-5">Monthly Attendance Progress{filterMonth !== "all" ? ` (${SM[parseInt(filterMonth, 10) - 1]})` : ""}</span><span className="attendancewidget-inline-6">{attRate}%</span></div>
            <div style={{ "--attendancewidget-5-background": T.border }} className="attendancewidget-dyn-5"><div style={{ "--attendancewidget-6-width": `${attRate}%` }} className="attendancewidget-dyn-6" /></div>
          </div>
        </>}

        {tab === "calendar" &&
        <div>
            <div className="attendancewidget-inline-7">
              <span className="attendancewidget-inline-8">{internMO[curMonth]} {yr}</span>
              <select className="filter-select attendancewidget-inline-9" value={curMonth} onChange={(e) => setCurMonth(Number(e.target.value))}>
                {internMO.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
            </div>
            <div className="attendancewidget-inline-10">
              {[["present", "#2563eb"], ["absent", "#ef4444"], ["wfh", "#3b82f6"], ["leave", "#f59e0b"], ["holiday", "#ea580c"]].map(([l, c]) => <div key={l} className="attendancewidget-inline-11"><div style={{ "--attendancewidget-7-background": c }} className="attendancewidget-dyn-7" /><span>{l}</span></div>)}
            </div>
            <table className="attendancewidget-inline-12">
              <thead><tr>{internWD.map((d) => <th key={d} className="attendancewidget-inline-13">{d}</th>)}</tr></thead>
              <tbody>
                {(() => {
                const dim = new Date(yr, curMonth + 1, 0).getDate();
                const fd = new Date(yr, curMonth, 1).getDay();
                let w = Array(fd).fill(null);
                const grid = [];
                for (let d = 1; d <= dim; d++) {w.push(d);if (w.length === 7) {grid.push(w);w = [];}}
                if (w.length) {while (w.length < 7) w.push(null);grid.push(w);}
                const getSt = (d) => {if (!d) return null;const dow = new Date(yr, curMonth, d).getDay();if (dow === 0 || dow === 6) return "weekend";const key = `${yr}-${String(curMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;return dailyData[key] || dailyAttendance[key] || null;};
                const sc = { present: "#eff6ff", absent: "#fef2f2", wfh: "#eff6ff", leave: "#fffbeb", holiday: "#fff7ed", weekend: "#f8fafc" };
                const cc = { present: "#2563eb", absent: "#ef4444", wfh: "#3b82f6", leave: "#d97706", holiday: "#ea580c", weekend: "#94a3b8" };
                return grid.map((row, wi) => <tr key={wi}>{row.map((d, di) => <td key={di} className="attendancewidget-inline-14">{d ? <div style={{ "--attendancewidget-8-background": sc[getSt(d)] || "transparent", "--attendancewidget-8-color": cc[getSt(d)] || "#334155" }} className="attendancewidget-dyn-8">{d}</div> : null}</td>)}</tr>);
              })()}
              </tbody>
            </table>
          </div>
        }

        {tab === "leave" && (
        filteredLeave.length === 0 ? <div className="empty-state">No leave requests match filters</div> :
        <div className="attendancewidget-inline-15"><table className="req-table"><thead><tr><th>Type</th><th>From</th><th>To</th><th>Reason</th><th>Status</th></tr></thead><tbody>
            {filteredLeave.map((r) => <tr key={r.id}><td className="attendancewidget-inline-16">{r.type}</td><td>{fmtDisp(r.from)}</td><td>{fmtDisp(r.to)}</td><td><div className="req-reason" title={r.reason}>{r.reason}</div></td><td><StatusBadge status={r.status} /></td></tr>)}
          </tbody></table></div>)
        }

        {tab === "permission" && (
        filteredPerm.length === 0 ? <div className="empty-state">No permission requests match filters</div> :
        <div className="attendancewidget-inline-17"><table className="req-table"><thead><tr><th>Date</th><th>From</th><th>To</th><th>Reason</th><th>Status</th></tr></thead><tbody>
            {filteredPerm.map((r) => <tr key={r.id}><td className="attendancewidget-inline-18">{fmtDisp(r.date)}</td><td>{r.from || r.fromTime || r.from_time || '-'}</td><td>{r.to || r.toTime || r.to_time || '-'}</td><td><div className="req-reason" title={r.reason}>{r.reason}</div></td><td><StatusBadge status={r.status} /></td></tr>)}
          </tbody></table></div>)
        }
      </div>

      {showLeaveForm && <InternModal title="Apply for Leave" onClose={() => { setShowLeaveForm(false); setSandwichWarn(""); setLeaveFromVal(""); setLeaveToVal(""); }}>
        <form onSubmit={handleLeaveSubmit}>
          <div className="form-group"><label className="form-label">Leave Type</label><select name="type" className="form-select" required><option value="">Select type</option>{leaveTypes.length ? leaveTypes.map((t) => <option key={t.leave_type} value={t.leave_type}>{t.leave_type}</option>) : <><option>Casual Leave</option><option>Sick Leave</option><option>Personal Leave</option><option>Comp Off</option><option>Earned Leave</option><option value="WFH">Work From Home</option></>}</select></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">From</label>
              <input name="from" type="date" className="form-input" required value={leaveFromVal}
                onChange={e => { setLeaveFromVal(e.target.value); checkSandwich(e.target.value, leaveToVal); }} />
            </div>
            <div className="form-group"><label className="form-label">To</label>
              <input name="to" type="date" className="form-input" required value={leaveToVal}
                onChange={e => { setLeaveToVal(e.target.value); checkSandwich(leaveFromVal, e.target.value); }} />
            </div>
          </div>
          {sandwichWarn && (
            <div style={{background:"#fffbeb",border:"1.5px solid #f59e0b",borderRadius:8,
              padding:"10px 14px",fontSize:13,color:"#92400e",marginBottom:8,display:"flex",gap:8,alignItems:"flex-start"}}>
              <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
              <div>
                <strong style={{display:"block",marginBottom:2}}>Sandwich Leave Detected</strong>
                {sandwichWarn}
              </div>
            </div>
          )}
          <div className="form-group"><label className="form-label">Reason</label><textarea name="reason" className="form-textarea" placeholder="Please provide the reason for your leave…" required /></div>
          <button type="submit" className="btn-submit">Submit Leave Request</button>
        </form>
      </InternModal>}

      {showPermForm && <InternModal title="Request Permission" onClose={() => setShowPermForm(false)}>
        <form onSubmit={handlePermSubmit}>
          <div className="form-group"><label className="form-label">Date</label><input name="date" type="date" className="form-input" required /></div>
          <div className="form-row"><div className="form-group"><label className="form-label">From Time</label><input name="from" type="time" className="form-input" required /></div><div className="form-group"><label className="form-label">To Time</label><input name="to" type="time" className="form-input" required /></div></div>
          <div className="form-group"><label className="form-label">Reason</label><textarea name="reason" className="form-textarea" placeholder="Please provide the reason…" required /></div>
          <button type="submit" className="btn-submit">Submit Permission Request</button>
        </form>
      </InternModal>}

      {/* ── Sandwich Leave Confirmation Modal ── */}
      {sandwichConfirm && pendingLeaveData && (
        <InternModal title="⚠️ Sandwich Leave Warning" onClose={() => { setSandwichConfirm(false); setPendingLeaveData(null); }}>
          <div style={{ background:"#fef2f2", border:"2px solid #fca5a5", borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
            <div style={{ fontWeight:800, fontSize:14, color:"#dc2626", marginBottom:8 }}>
              ⚠️ This is a Sandwich Leave
            </div>
            <div style={{ fontSize:13, color:"#7f1d1d", lineHeight:1.7 }}>
              {sandwichWarn}
            </div>
          </div>
          <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:"12px 14px", marginBottom:16, fontSize:13, color:"#7c2d12", lineHeight:1.7 }}>
            <b>📋 What this means:</b><br/>
            The weekend / holiday days within or adjacent to your leave period will <b>also be counted as Leave Without Pay (LWP)</b> as per company policy.
            <br/><br/>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginTop:8 }}>
              <div style={{ background:"#fff", borderRadius:6, padding:"6px 10px" }}>
                <div style={{ fontSize:10, color:"#94a3b8", fontWeight:600 }}>From</div>
                <div style={{ fontWeight:700, fontSize:13 }}>{new Date(pendingLeaveData.fromDate).toLocaleDateString("en-IN",{weekday:"short",day:"2-digit",month:"short"})}</div>
              </div>
              <div style={{ background:"#fff", borderRadius:6, padding:"6px 10px" }}>
                <div style={{ fontSize:10, color:"#94a3b8", fontWeight:600 }}>To</div>
                <div style={{ fontWeight:700, fontSize:13 }}>{new Date(pendingLeaveData.toDate).toLocaleDateString("en-IN",{weekday:"short",day:"2-digit",month:"short"})}</div>
              </div>
            </div>
          </div>
          <p style={{ fontSize:12, color:"#64748b", marginBottom:16 }}>
            Do you still want to apply? It will be submitted and processed as <b style={{color:"#dc2626"}}>LWP (Leave Without Pay)</b>.
          </p>
          <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <button type="button" className="btn-outline"
              onClick={() => { setSandwichConfirm(false); setPendingLeaveData(null); }}>
              Cancel — Go Back
            </button>
            <button type="button"
              onClick={() => submitLeaveAPI(pendingLeaveData)}
              style={{ background:"#dc2626", color:"#fff", border:"none", borderRadius:8,
                padding:"9px 20px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              Yes, Apply as LWP
            </button>
          </div>
        </InternModal>
      )}
    </div>);

}

export default AttendanceWidget;